import { PanelCard, Placeholder, RegaraNoteList, TitledSection, withInlinePlaceholders } from './DossierPrimitives';

/**
 * PSUR (MDR Article 86) template renderer.
 *
 * Mirrors _render_psur() in backend/services/dossier/eu_combined_renderer/__init__.py.
 *
 * Renders:
 *   - A header/admin block: device_class, cadence_note, manufacturer, SRN,
 *     basic_udi_di, device_name, reporting_period, psur_number.
 *   - Numbered sections with §N Title heading + content (no "Content:" label),
 *     matching Python: `§{number} {title}` with content directly below.
 *   - Notes list at the bottom.
 *
 * Placeholder discipline: all fields use PsurHeaderCell (which defers to the
 * shared Placeholder chip for __PLACEHOLDER__ or null values), so "to complete"
 * cells appear identically to the PDF renderer.
 *
 * Data shape (PsurTemplate from app/dossier/eu/documents/psur.py):
 *   { device_class, cadence_note, manufacturer, srn, basic_udi_di, device_name,
 *     reporting_period, psur_number, sections: PsurSection[], notes: string[] }
 *   PsurSection: { number, title, content }
 */

const SUBMITTER_MARKER = '[Submitter must complete]';
const PLACEHOLDER_TOKEN = '__PLACEHOLDER__';

function psurCellContent(value: unknown, fallbackHint?: string): React.ReactNode {
  if (value == null || value === '') {
    return <Placeholder hint={fallbackHint ?? null} />;
  }
  const s = String(value).trim();
  if (s === SUBMITTER_MARKER) {
    return <Placeholder hint={fallbackHint ?? null} />;
  }
  if (s === PLACEHOLDER_TOKEN) {
    return <Placeholder hint={fallbackHint ?? null} />;
  }
  if (s.startsWith(`${PLACEHOLDER_TOKEN}:`)) {
    const hint = s.slice(PLACEHOLDER_TOKEN.length + 1).trim() || fallbackHint || null;
    return <Placeholder hint={hint} />;
  }
  return withInlinePlaceholders(s);
}

type PsurSection = {
  number?: string | null;
  title?: string | null;
  content?: string | null;
};

export function PsurRenderer({ psur }: { psur: Record<string, unknown> }) {
  const sections = Array.isArray(psur.sections) ? (psur.sections as PsurSection[]) : [];
  const notes = Array.isArray(psur.notes) ? (psur.notes as string[]) : [];

  return (
    <div className="space-y-4">
      {/* Header / admin block */}
      <PanelCard>
        <div className="text-xs tracking-[0.16em] uppercase text-muted font-bold mb-1.5">
          Periodic Safety Update Report (Article 86)
        </div>
        <p className="text-[11px] text-muted m-0 mb-4">
          MDR Article 86 / MDCG 2022-21 — Periodic Safety Update Report
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <PsurHeaderCell label="Device class (EU MDR)" value={psur.device_class} />
          <PsurHeaderCell label="Reporting cadence" value={psur.cadence_note} />
          <PsurHeaderCell
            label="Manufacturer"
            value={psur.manufacturer}
            fallbackHint="Legal manufacturer name"
          />
          <PsurHeaderCell
            label="Single Registration Number (SRN)"
            value={psur.srn}
            fallbackHint="Assigned upon EUDAMED registration"
          />
          <PsurHeaderCell
            label="Basic UDI-DI"
            value={psur.basic_udi_di}
            fallbackHint="Assigned upon EUDAMED registration"
          />
          <PsurHeaderCell
            label="Device"
            value={psur.device_name}
            fallbackHint="Device name / trade name"
          />
          <PsurHeaderCell
            label="Reporting period"
            value={psur.reporting_period}
            fallbackHint="e.g. 2024-01-01 to 2024-12-31"
          />
          <PsurHeaderCell
            label="PSUR number"
            value={psur.psur_number}
            fallbackHint="PSUR document reference number"
          />
        </div>
      </PanelCard>

      {/* Numbered sections — matching Python: §{number} {title} + content directly below */}
      {sections.map((sec, idx) => {
        const numStr = sec.number ?? String(idx + 1);
        const title = sec.title ?? '';
        return (
          <TitledSection key={idx} title={`§${numStr} ${title}`}>
            <div className="text-[13.5px] leading-[1.6]">
              {psurCellContent(sec.content ?? null, `${title} — to be completed by the manufacturer`)}
            </div>
          </TitledSection>
        );
      })}

      <RegaraNoteList notes={notes} />
    </div>
  );
}

// ── Header cell helper ────────────────────────────────────────────────────────

function PsurHeaderCell({
  label,
  value,
  fallbackHint,
}: {
  label: string;
  value: unknown;
  fallbackHint?: string;
}) {
  const v = value == null ? '' : String(value).trim();
  const isPlaceholderMarker = v === PLACEHOLDER_TOKEN || v.startsWith(`${PLACEHOLDER_TOKEN}:`);
  const isSubmitterText = v === '[Submitter must complete]';
  const isEmpty = v === '';

  let hint: string | null = null;
  if (isPlaceholderMarker) {
    if (v.startsWith(`${PLACEHOLDER_TOKEN}:`)) {
      hint = v.slice(PLACEHOLDER_TOKEN.length + 1).trim() || fallbackHint || label;
    } else {
      hint = fallbackHint || label;
    }
  }

  return (
    <div className="bg-[#f8f9fa] rounded-md py-2.5 px-3">
      <div className="text-[10px] uppercase tracking-[0.12em] font-bold text-muted mb-0.5">
        {label}
      </div>
      <div className="text-[13px] text-navy">
        {isPlaceholderMarker || isSubmitterText ? (
          <Placeholder hint={hint ?? fallbackHint ?? label} />
        ) : isEmpty ? (
          <Placeholder hint={fallbackHint || label} />
        ) : (
          withInlinePlaceholders(v)
        )}
      </div>
    </div>
  );
}
